import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle2, Coffee, Home, Loader2 } from "lucide-react";
import { api, formatApiError } from "@/lib/api";

const POLL_INTERVAL_MS = 2000;
const MAX_POLLS = 10;

export const PaymentSuccessPage = () => {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [state, setState] = useState({ phase: "polling", data: null, error: "" });
  const [polls, setPolls] = useState(0);

  useEffect(() => {
    if (!sessionId) {
      setState({ phase: "error", error: "Lipsește session_id în URL." });
      return;
    }
    let cancelled = false;

    const check = async () => {
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (cancelled) return;
        if (data.payment_status === "paid") {
          setState({ phase: "paid", data, error: "" });
          return;
        }
        if (data.status === "expired" || data.payment_status === "failed") {
          setState({ phase: "failed", data, error: "Plata nu a fost finalizată." });
          return;
        }
        setPolls((n) => n + 1);
      } catch (err) {
        if (cancelled) return;
        setState({ phase: "error", error: formatApiError(err, "Nu am putut verifica plata") });
      }
    };

    if (polls === 0) check();
    if (state.phase !== "polling") return;
    if (polls >= MAX_POLLS) {
      setState({ phase: "timeout", data: null, error: "" });
      return;
    }
    const t = setTimeout(check, POLL_INTERVAL_MS);
    return () => {
      cancelled = true;
      clearTimeout(t);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId, polls]);

  return (
    <main data-testid="payment-success" className="mx-auto flex min-h-[70vh] max-w-2xl flex-col items-center justify-center px-6 py-20 text-center">
      {state.phase === "polling" && (
        <div data-testid="payment-polling" className="animate-fade-up">
          <Loader2 className="mx-auto h-10 w-10 animate-spin text-[var(--coral)]" strokeWidth={1.4} />
          <h1 className="mt-6 font-serif text-3xl font-light text-[#F7F1E8]">Verific plata&hellip;</h1>
          <p className="mt-3 text-sm text-[#F7F1E8]/78">Doar câteva secunde.</p>
        </div>
      )}

      {state.phase === "paid" && (
        <div data-testid="payment-paid" className="animate-fade-up">
          <div className="relative inline-flex">
            <div className="absolute inset-0 -z-10 animate-pulse rounded-full bg-[var(--tint-mint)] blur-xl" />
            <CheckCircle2 className="h-16 w-16 text-[var(--teal)]" strokeWidth={1.3} />
          </div>
          <p className="mt-8 text-xs uppercase tracking-[0.35em] text-[var(--accent)]">Mulțumesc din suflet</p>
          <h1 className="mt-4 font-serif text-5xl font-light leading-tight text-[#F7F1E8]">
            {state.data?.buyer_name ? `Mulțumesc, ${state.data.buyer_name}.` : "Mulțumesc!"}
          </h1>
          <p className="mt-4 text-lg font-light text-[#F7F1E8]/86">
            {state.data?.tier_label ? `Am primit „${state.data.tier_label}".` : "Am primit plata."} Îți scriu curând
            pe e-mail cu următorii pași.
          </p>
          <div className="mt-10 flex flex-wrap items-center justify-center gap-3">
            <Link
              to="/"
              data-testid="success-home-btn"
              className="inline-flex items-center gap-2 rounded-full bg-[var(--accent)] px-6 py-3 text-xs font-medium uppercase tracking-[0.2em] text-white transition-transform hover:-translate-y-0.5 active:scale-95"
            >
              <Home className="h-3.5 w-3.5" strokeWidth={1.6} /> Înapoi la jurnal
            </Link>
            <Link
              to="/sponsorizeaza"
              className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-[#F7F1E8]/78 hover:text-[#F7F1E8]"
            >
              <Coffee className="h-3.5 w-3.5" strokeWidth={1.6} /> Cumpără-mi o cafea
            </Link>
          </div>
        </div>
      )}

      {(state.phase === "failed" || state.phase === "error" || state.phase === "timeout") && (
        <div data-testid="payment-issue" className="animate-fade-up">
          <p className="text-xs uppercase tracking-[0.3em] text-[var(--accent)]">O secundă</p>
          <h1 className="mt-4 font-serif text-4xl font-light text-[#F7F1E8]">
            {state.phase === "timeout" ? "Plata durează puțin mai mult decât de obicei." : "Ceva n-a mers cum trebuia."}
          </h1>
          <p className="mt-3 text-sm text-[#F7F1E8]/78">
            {state.error || "Reîncarcă pagina peste câteva secunde sau încearcă din nou."}
          </p>
          <div className="mt-8 flex justify-center gap-3">
            <Link
              to="/sponsorizeaza"
              className="rounded-full bg-[var(--accent)] px-6 py-3 text-xs uppercase tracking-[0.2em] text-white"
              data-testid="issue-retry-btn"
            >
              Încearcă din nou
            </Link>
            <Link
              to="/"
              className="rounded-full border border-[color-mix(in_srgb,var(--milk)_20%,transparent)] px-6 py-3 text-xs uppercase tracking-[0.2em] text-[#F7F1E8]/86"
            >
              Înapoi la jurnal
            </Link>
          </div>
        </div>
      )}
    </main>
  );
};

export default PaymentSuccessPage;
